import React, { useState, useEffect } from 'react';
import StudentDetails from './FormSections/StudentDetails';
import EducationBackground from './FormSections/EducationBackground';
import AdmissionDetails from './FormSections/AdmissionDetails';
import StaffOverview from './FormSections/StaffOverview';
import Message from './common/Message';
import { fetchBdeEmployees, submitStudentData, fetchCourses } from '../../services/dwapi';
import { validateForm } from '../../utils/dwvalidation';
import { initialFormData } from '../../utils/dwconstants';
import '../../styles/DirectWalkin.css';

const DirectWalkin = () => {
  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState({ type: '', message: '' });
  const [sections, setSections] = useState({
    studentDetails: false,
    educationBackground: false,
    admissionDetails: false,
    staffOverview: false
  });
  const [bdeEmployees, setBdeEmployees] = useState([]);
  const [filteredCourses, setFilteredCourses] = useState([]);
  const [courses, setCourses] = useState([]);
  const [courseTypes, setCourseTypes] = useState([]);

  // Load BDE employees when branch changes
  useEffect(() => {
    if (!formData.branch) return;
    
    fetchBdeEmployees(formData.branch)
      .then(data => {
        setBdeEmployees(data);
      })
      .catch(error => {
        console.error("Failed to fetch BDE employees:", error);
      });
  }, [formData.branch]);

  useEffect(() => {
    fetchCourses()
      .then((data) => {
        setCourses(data);
        const types = [...new Set(data.map(course => course.courseType))];
        setCourseTypes(types);
      })
      .catch((error) => {
        console.error('Failed to load courses:', error);
      });
  }, []);

  // Handle form field changes
  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (errors[name]) {
      setErrors({ ...errors, [name]: "" });
    }
  };

  // Handle course type selection and filter courses
  const handleCourseTypeChange = (e) => {
    const type = e.target.value;

    setFormData(prev => ({
      ...prev,
      courseType: type,
      course: "" // Reset course selection when type changes
    }));

    // Filter courses based on selected type
    const filtered = courses.filter(course => course.courseType === type);
    setFilteredCourses(filtered);
  };

  // Toggle section visibility
  const toggleSection = (section) => {
    setSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validateForm(formData);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    // Final check to ensure source is set
    const submissionData = {
      ...formData,
      source: formData.source || 'Direct'
    };

    setIsSubmitting(true);
    try {
      await submitStudentData(submissionData);
      setSubmitMessage({ type: "success", message: "Student registration successful!" });
      alert("Student registration successful!");
      setFormData(initialFormData);
    } catch (error) {
      setSubmitMessage({ type: "error", message: "Failed to register student. Please try again." });
      alert("Failed to register student. Please try again");
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setSubmitMessage({ type: "", message: "" }), 5000);
    }
  };

  return (
    <div className="direct-walkin-container">
      <h1 className="form-title">ENQUIRE FORM</h1>

      {submitMessage.message && (
        <Message type={submitMessage.type} message={submitMessage.message} />
      )}

      <form onSubmit={handleSubmit} className="walkin-form">
        <StudentDetails 
          formData={formData}
          handleChange={handleChange}
          errors={errors}
          isVisible={sections.studentDetails}
          toggleSection={() => toggleSection('studentDetails')}
        />

        <EducationBackground 
          formData={formData}
          handleChange={handleChange}
          errors={errors}
          isVisible={sections.educationBackground}
          toggleSection={() => toggleSection('educationBackground')}
        />

        <AdmissionDetails 
          formData={formData}
          handleChange={handleChange}
          handleCourseTypeChange={handleCourseTypeChange}
          errors={errors}
          courseTypes={courseTypes}
          filteredCourses={filteredCourses}
          isVisible={sections.admissionDetails}
          toggleSection={() => toggleSection('admissionDetails')}
        />

        <StaffOverview 
          formData={formData}
          handleChange={handleChange}
          bdeEmployees={bdeEmployees}
          isVisible={sections.staffOverview}
          toggleSection={() => toggleSection('staffOverview')}
        />

        <div className="form-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setFormData(initialFormData)}
          >
            Reset
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Submitting...' : 'Register Student'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default DirectWalkin;